// Pairing state for a fresh or unpaired web player.
import { useState } from 'react'
import DisconnectCode from './DisconnectCode'

interface Props {
  pairingCode: string | null
  // Set when this pairing screen is the result of a disconnect (license expiry /
  // removal), so the operator sees why. null on a fresh first-time setup.
  disconnectCode?: number | null
}

const STEPS = [
  'Open the WeTook dashboard',
  'Click "Add Screen"',
  'Enter the code above',
]

// Inline SVG so the webplayer doesn't pull lucide-react — same rule as
// WifiBadge and SettingsDrawer. Stroke-only paths matching Lucide's
// clipboard / check shapes.
function ClipboardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function CopyableCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      // navigator.clipboard is unavailable on insecure origins and in some TV
      // browsers. The fallback keeps the button honest there rather than
      // throwing; if BOTH fail we simply don't claim success.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code)
      } else {
        const ta = document.createElement('textarea')
        ta.value = code
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — the code is still on screen to read */
    }
  }

  return (
    <div className="flex w-full items-center justify-center gap-3 sm:gap-4">
      {/* The code stays plain text. It is the thing being read off the screen
          from across a room, so it should not also look pressable — the icon
          beside it carries the action. */}
      <span
        className="max-w-full whitespace-nowrap text-white font-black"
        style={{ fontSize: 'clamp(2.65rem, 17vw, 4.5rem)', letterSpacing: '0.14em', lineHeight: 1 }}
      >
        {code}
      </span>
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied' : 'Copy pairing code'}
        aria-label={copied ? 'Pairing code copied' : `Copy pairing code ${code}`}
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors ${
          copied
            ? 'border-transparent bg-white/15 text-white'
            : 'border-white/20 text-muted hover:border-white/40 hover:text-white'
        } focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand`}
      >
        {copied ? <CheckIcon /> : <ClipboardIcon />}
      </button>
      {/* Announced for screen readers; the icon swap is the visual feedback. */}
      <span className="sr-only" aria-live="polite">{copied ? 'Copied' : ''}</span>
    </div>
  )
}

export default function SetupScreen({ pairingCode, disconnectCode }: Props) {
  if (!pairingCode) {
    return (
      <div className="flex min-h-full w-full max-w-sm flex-col items-center justify-center gap-4 px-5 py-6 text-center">
        <div className="h-10 w-10 rounded-full border-[3px] border-brand border-t-transparent animate-spin" />
        <h2 className="text-white text-[clamp(1.05rem,5vw,1.25rem)] font-semibold">Setting up display...</h2>
        <p className="text-muted text-sm">Connecting to WeTook server...</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-full w-full max-w-3xl flex-col items-center justify-center gap-[clamp(1.75rem,7vh,3rem)] px-5 py-6 text-center sm:px-8">
      <h1 className="text-white text-[clamp(1.4rem,7vw,1.875rem)] font-bold tracking-tight">Pair Device</h1>
      {disconnectCode != null && <DisconnectCode code={disconnectCode} />}

      <div className="flex w-full flex-col items-center gap-2">
        <span className="text-muted text-[11px] font-medium uppercase tracking-[0.18em] sm:text-xs">
          Pairing Code
        </span>
        {/* Copy button beside the code. On a TV nobody can click it and nothing
            is lost; on the laptops and tablets people actually pair from, the
            operator was retyping six characters into another tab from memory,
            where O/0 and I/1 at this weight are a coin flip. */}
        <CopyableCode code={pairingCode} />
      </div>

      <ol className="grid w-full max-w-sm gap-2 sm:max-w-none sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-3">
        {STEPS.map((label, i) => (
          <li
            key={i}
            className="flex w-full items-center gap-2 rounded-lg bg-surface px-3.5 py-2 text-left sm:w-auto sm:rounded-full sm:px-4"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand text-white text-xs font-bold">
              {i + 1}
            </span>
            <span className="text-white text-[13px] sm:text-sm">{label}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
